import React from 'react';
import { ClientProfileViewModel } from '@common/model/client';
import { ClientType } from '@common/model/client/clientType';
import { SearchIcon } from '@cp/ds/src/icons/mobileMenu/search';
import { SearchActiveIcon } from '@cp/ds/src/icons/mobileMenu/searchActive';
import { MessagesIcon } from '@cp/ds/src/icons/mobileMenu/messages';
import { MessagesActiveIcon } from '@cp/ds/src/icons/mobileMenu/messagesActive';
import { SigninIcon } from '@cp/ds/src/icons/mobileMenu/signin';
import { SigninActiveIcon } from '@cp/ds/src/icons/mobileMenu/signinActive';
import { VacanciesIcon } from '@cp/ds/src/icons/mobileMenu/vacancies';
import { VacanciesActiveIcon } from '@cp/ds/src/icons/mobileMenu/vacanciesActive';
import { InviteIcon } from '@cp/ds/src/icons/mobileMenu/invite';
import { InviteActiveIcon } from '@cp/ds/src/icons/mobileMenu/inviteActive';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { DialogsFilterType } from '@common/model/dialog/filter';
import { MenuItemWithTimedHints } from '../ui/menuItemWithTimedHints';
import { MenuItemProps } from './item.interface';
import { ItemsCreatorProps, ItemsCreatorReturn } from './types';

export const createClientItems = ({
  clientProfile,
  generateAppPath,
  routeParams,
  unreadMessagesCount,
  isLoadingNavigate,
  navigateToCatalog,
  isProfileTabActive,
  onProfileTabClick,
  onMessagesTabClick,
  queryParams,
  menuContainerRef,
}: ItemsCreatorProps & {
  clientProfile: ClientProfileViewModel;
}): ItemsCreatorReturn => {
  const isSpecialist = clientProfile?.type === ClientType.Specialist;
  const { pageId: currentPageId } = routeParams;

  const items: MenuItemProps[] = [];

  if (isLoadingNavigate) {
    return { items, isLoading: isLoadingNavigate };
  }

  const isSearchActive = currentPageId === ClientWebPage.Catalog && !isProfileTabActive;

  items.push({
    title: isSpecialist ? 'Работа' : 'Помощники',
    clientProfile,
    icon: isSearchActive ? SearchActiveIcon : SearchIcon,
    onClick: navigateToCatalog,
    isActiveItem: isSearchActive,
  });

  const isMyVacanciesPageActive = currentPageId === ClientWebPage.ProfileVacancies && !isProfileTabActive;
  const isMessagesPageActive = currentPageId === ClientWebPage.ProfileMessages && !isProfileTabActive;
  const isDealsActive = isMessagesPageActive && 'filter' in queryParams && queryParams.filter === DialogsFilterType.Deals;

  if (clientProfile.type === ClientType.Specialist) {
    items.push({
      title: 'Заказы',
      clientProfile,
      icon: isDealsActive ? InviteActiveIcon : InviteIcon,
      href: generateAppPath({ page: ClientWebPage.ProfileMessages, queryParams: { filter: DialogsFilterType.Deals } }),
      isActiveItem: isDealsActive,
    });
  }

  if (clientProfile.type === ClientType.Employer) {
    items.push({
      title: 'Объявления',
      clientProfile,
      icon: isMyVacanciesPageActive ? VacanciesActiveIcon : VacanciesIcon,
      href: generateAppPath({ page: ClientWebPage.ProfileVacancies }),
      isActiveItem: isMyVacanciesPageActive,
      renderItem: ({ config }) =>
        React.createElement(MenuItemWithTimedHints, {
          config,
          menuContainerRef,
          hints: 'Объявление',
          delayToShowInMs: 10000,
          tooltipProps: {
            bordered: true,
            slotProps: {
              popper: {
                disablePortal: true,
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [-50, 0],
                    },
                  },
                ],
              },
            },
          },
        }),
    });
  }

  items.push({
    title: 'Сообщения',
    clientProfile,
    icon: isMessagesPageActive ? MessagesActiveIcon : MessagesIcon,
    href: generateAppPath({ page: ClientWebPage.ProfileMessages }),
    onClick: onMessagesTabClick,
    badgeContent: unreadMessagesCount,
    isActiveItem: isMessagesPageActive,
  });

  items.push({
    title: 'Профиль',
    clientProfile,
    isProfile: true,
    icon: isProfileTabActive ? SigninActiveIcon : SigninIcon,
    isActiveItem: isProfileTabActive,
    onClick: onProfileTabClick,
  });

  return { items, isLoading: isLoadingNavigate };
};
